import { sendProposal, submitProposal } from '@/api/Proposal';
import type { IConfig } from '@/components/forms/interface/IConfig';
import type { ILink } from '@/components/forms/interface/ILink';
import { defineStore, createPinia, setActivePinia } from 'pinia';
import { reactive, ref } from 'vue';

setActivePinia(createPinia());

export const appStore = defineStore('appStore', () => {
  const config = ref<IConfig>();
  const activeLink = ref<ILink>();
  const global = ref<any>();

  function getConfig() {
    return config.value;
  }

  function setConfig(value: IConfig) {
    return config.value = value;
  }

  function setActiveLink(value?: ILink) {
    return activeLink.value = value;
  }

  function setGlobal(value: any) {
    return global.value = value;
  }


  return {
    setConfig,
    getConfig,
    setActiveLink,
    activeLink,
    setGlobal,
    global
  };
});

export const loaderStore = defineStore('loaderStore', () => {
  const opened = ref(false);
  function isOpen() {
    return opened.value;
  }

  function setOpen(value: boolean) {
    return opened.value = value;
  }

  return {
    isOpen,
    setOpen
  };
});

export const snackbarStore = defineStore('snackbarStore', () => {
  const opened = ref(false);
  const color = ref('');
  const text = ref('');
  const timeout = ref(3000);
  const timeoutControl = ref();

  function isOpen() {
    return opened.value;
  }

  function success(msg: string) {
    show(msg, 'success');
  }

  function error(msg: string) {
    show(msg, 'error');
  }

  function alert(msg: string) {
    show(msg, 'warning');
  }

  function show(msg: string, snackColor: string, snackTimeout = 2500) {
    clearTimeout(timeoutControl.value);
    text.value = msg;
    color.value = snackColor;
    opened.value = true;
    timeout.value = snackTimeout;
    timeoutControl.value = setTimeout(close, timeout.value);
  }

  function close() {
    clearTimeout(timeoutControl.value);
    opened.value = false;
    color.value = '';
    text.value = '';
    timeout.value = 2500;
  }

  return {
    isOpen,
    color,
    text,
    timeout,
    success,
    error,
    alert,
    show,
    close
  };
});

export const stepStore = defineStore('stepStore', () => {
  const step = ref('course');
  const lastStep = ref<string[]>([]);

  function nextStep(nextStep: string) {
    snackbarStore().close();
    lastStep.value.unshift(step.value);
    step.value = nextStep;
  }

  function previouStep() {
    snackbarStore().close();
    step.value = lastStep.value[0];
    lastStep.value.shift();
  }

  function getStep() {
    return step.value;
  }

  function reset() {
    step.value = 'course';
    lastStep.value = [];
  }

  return {
    nextStep,
    previouStep,
    getStep,
    reset
  };
});

export const proposalStore = defineStore('proposalStore', () => {
  const proposalStore = reactive<{
    id?: number,
    guarantor_id?: number,
    guardian_id?: number,
    simulation_id?: number,
    token?: string,
    submitted?: boolean
  }>({});

  function setId(id: number) {
    proposalStore.id = id;
  }

  function setGuarantorId(guarantorId: number) {
    proposalStore.guarantor_id = guarantorId;
  }

  function setProposal(data) {
    const { id, guarantor_id, guardian_id, simulation_id, token, submitted } = data;
    Object.assign(proposalStore, { id, guarantor_id, guardian_id, simulation_id, token, submitted });
  }

  function setSimulationId(simulationId: number) {
    proposalStore.simulation_id = simulationId;
  }

  function setToken(token: string) {
    proposalStore.token = token;
  }

  function getProposal() {
    return proposalStore;
  }

  function reset() {
    Object.assign(proposalStore, {});
  }

  async function send(data = {}): Promise<string> {
    const token = await sendProposal(proposalStore.id, data);
    setToken(token);
    return token;
  }

  async function submit() {
    return submitProposal(proposalStore.id);
  }

  return {
    setId,
    setGuarantorId,
    setSimulationId,
    setToken,
    getProposal,
    setProposal,
    send,
    submit,
    reset
  };
});
